// @flow

import {
  type IncomingPayment,
  type IncomingPaymentStatus,
  type OpenedIncomingPayment,
  getStatus,
  getCountry,
  getSafePaymentOptions,
  getSafePaymentType,
  getSafePaymentTypeCode,
} from '@tectonics/prs/incoming-payment';

import { type Feature } from '@tectonics/prs/feature';
import { type PaymentMethod } from '@tectonics/prs/payment-method';
import { type Country } from '@tectonics/prs/country';
import { type PaymentSpec } from '@tectonics/prs/payment-spec';
import { type PaymentOption } from '@tectonics/prs/incoming-payment/payment-option';

import { type PaymentSpecGenerator } from './types';

type GenerateSpecArgs = {|
  +incomingPayment: IncomingPayment,
  +generator: PaymentSpecGenerator,
  +lookAndFeelCode: string,
|};

export function generatePaymentSpec({ incomingPayment, generator, lookAndFeelCode }: GenerateSpecArgs): PaymentSpec {
  const status = getStatus(incomingPayment);
  const country = getCountry(incomingPayment);

  return {
    country,
    features: generator.features({ status, country, incomingPayment, lookAndFeelCode }),
    paymentMethods: generatePaymentMethods({ status, country, incomingPayment, generator, lookAndFeelCode }),
  };
}

type GeneratePaymentMethodsArgs = {|
  +status: IncomingPaymentStatus,
  +country: Country,
  +incomingPayment: IncomingPayment,
  +generator: PaymentSpecGenerator,
  +lookAndFeelCode: string,
|};

function generatePaymentMethods({
  incomingPayment,
  status,
  country,
  generator,
  lookAndFeelCode,
}: GeneratePaymentMethodsArgs): ImmutableArray<PaymentMethod> {
  switch (incomingPayment.status) {
    case 'OP': {
      const paymentOptions = getSafePaymentOptions(incomingPayment);
      return generatePaymentOptionsBasedPaymentMethods({
        paymentOptions,
        country,
        incomingPayment,
        generator,
        lookAndFeelCode,
      });
    }

    case 'CO':
    case 'PE': {
      const paymentType = getSafePaymentType(incomingPayment);
      return [
        {
          code: paymentType,
          features: generatePaymentTypeBasedFeatures({
            paymentType,
            country,
            status,
            incomingPayment,
            generator,
            lookAndFeelCode,
          }),
        },
      ];
    }

    default:
      return [];
  }
}

type GeneratePaymentOptionsBasedPaymentMethodsArgs = {|
  +paymentOptions: ImmutableArray<PaymentOption>,
  +country: Country,
  +incomingPayment: OpenedIncomingPayment,
  +generator: PaymentSpecGenerator,
  +lookAndFeelCode: string,
|};

function generatePaymentOptionsBasedPaymentMethods({
  paymentOptions,
  country,
  incomingPayment,
  generator,
  lookAndFeelCode,
}: GeneratePaymentOptionsBasedPaymentMethodsArgs): ImmutableArray<PaymentMethod> {
  const paymentMethods: PaymentMethod[] = [];

  paymentOptions
    .filter(paymentOption => paymentOption.id in generator)
    .forEach(paymentOption => {
      const features = generator[paymentOption.id]({ country, status: 'OP', incomingPayment, lookAndFeelCode });
      if (features) {
        paymentMethods.push({
          code: paymentOption.id,
          features,
        });
      }
    });

  return paymentMethods;
}

type GeneratePaymentTypeBasedFeaturesArgs = {|
  +status: IncomingPaymentStatus,
  +paymentType: string,
  +country: Country,
  +incomingPayment: IncomingPayment,
  +generator: PaymentSpecGenerator,
  +lookAndFeelCode: string,
|};

function generatePaymentTypeBasedFeatures({
  status,
  paymentType,
  country,
  incomingPayment,
  generator,
  lookAndFeelCode,
}: GeneratePaymentTypeBasedFeaturesArgs): ImmutableArray<Feature> {
  if (paymentType in generator) {
    const paymentTypeCode = getSafePaymentTypeCode(incomingPayment);
    const features = generator[paymentType]({ country, status, incomingPayment, paymentTypeCode, lookAndFeelCode });
    if (!features) throw new Error(`Payment Spec Generator - There are no features for "${paymentType}"`);
    return features;
  }

  return [];
}
